import { APIError } from '../../error/errorHandler';
import type { Message, ChatContext } from '../../../types/models';
import { usageService } from '../../../services/UsageService';
import { formatMessages } from './messageFormatter';
import { getProviderConfig, getProviderUrl, getProviderEndpoint } from '../../../config/modelsDatabase';
import type { MinimaxRequestBody, MinimaxResponse, MinimaxImageGenerationResponse } from './types';

// Helper function to estimate token count from text
function estimateTokenCount(text: string): number {
  // Rough estimation: ~4 characters per token on average
  return Math.ceil(text.length / 4);
}

// Generate an image using the Minimax image generation API
export async function generateImage(
  prompt: string,
  apiKey: string,
  userId?: string,
  topUpValue?: number,
  aspectRatio: string = "1:1",
  n: number = 1
): Promise<string[]> {
  try {
    // First, check if we have the required parameters
    if (!apiKey) {
      throw new APIError('API key is required', 'Minimax');
    }
    
    // Format the authorization header
    const headers = {
      'Authorization': `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    };
    
    // Create the request body
    const body = {
      model: "image-01",
      prompt,
      aspect_ratio: aspectRatio,
      response_format: "url",
      n,
      prompt_optimizer: true
    };
    
    const apiUrl = 'https://api.minimaxi.chat/v1/image_generation';
    console.log(`Sending image generation request to Minimax: ${apiUrl}`);
    
    // Make the request to Minimax API
    const startTime = Date.now();
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    console.log(`Minimax Image API response time: ${Date.now() - startTime}ms`);
    console.log(`Minimax Image response status: ${response.status}`);
    
    if (!response.ok) {
      let errorMessage = `Minimax Image API Error: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error('Minimax image error response:', errorData);
        
        if (errorData.base_resp) {
          errorMessage = `Minimax error: ${errorData.base_resp.status_msg || 'Unknown error'}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        }
      } catch (e) {
        console.error('Failed to parse error response:', e);
      }
      
      throw new APIError(errorMessage, 'Minimax');
    }
    
    // Parse the response
    const data = await response.json() as MinimaxImageGenerationResponse;
    console.log('Minimax image generation response:', data);
    
    // Check if we have image URLs
    if (!data.data?.image_urls || !Array.isArray(data.data.image_urls) || data.data.image_urls.length === 0) {
      throw new APIError('No images were generated', 'Minimax');
    }
    
    // Record usage if we have user ID
    if (userId && typeof topUpValue === 'number') {
      try {
        // Approximated usage cost for images
        const imageCost = n * 1000; // Assuming 1000 tokens per image for cost calculation
        
        const usage = {
          prompt_tokens: estimateTokenCount(prompt),
          completion_tokens: imageCost,
          total_tokens: estimateTokenCount(prompt) + imageCost
        };
        
        await usageService.recordUsage(userId, 'minimax-image-01', usage, topUpValue);
        console.log('Recorded Minimax image usage');
      } catch (err) {
        console.error('Failed to record Minimax image usage:', err);
      }
    }
    
    return data.data.image_urls;
  } catch (error) {
    console.error('Minimax Image API error:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    throw new APIError(
      error instanceof Error ? error.message : 'Failed to communicate with Minimax Image API',
      'Minimax'
    );
  }
}

export async function sendMessage(
  message: string,
  apiKey: string,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[],
  userId?: string,
  topUpValue?: number
): Promise<string> {
  try {
    // First, check if we have the required parameters
    if (!apiKey) {
      throw new APIError('API key is required', 'Minimax');
    }
    
    // Get Minimax configuration
    const config = getProviderConfig('minimax');
    if (!config) {
      throw new APIError('Minimax provider configuration not found', 'Minimax');
    }

    const chatEndpoint = getProviderEndpoint('minimax', 'chat');
    if (!chatEndpoint) {
      throw new APIError('Minimax chat endpoint not found', 'Minimax');
    }
    
    // Format the messages for Minimax API
    const formattedMessages = formatMessages(message, context, previousMessages);
    
    // Format the authorization header exactly like the curl example
    const headers = {
      'Authorization': `Bearer ${apiKey}`, // Use Bearer prefix as shown in the curl example
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    
    const body: MinimaxRequestBody = {
      model: modelId,
      messages: formattedMessages,
      stream: false,
      max_tokens: 4096,
      temperature: 0.7,
      top_p: 0.95
    };
    
    // IMPORTANT: Temporarily bypass the proxy for testing
    // We'll call the Minimax API directly just like our test script
    const directApiUrl = 'https://api.minimaxi.chat/v1/text/chatcompletion_v2';
    console.log(`DIRECT API CALL: Sending request directly to Minimax: ${directApiUrl}`);
    
    // Make the request DIRECTLY to Minimax API
    const startTime = Date.now();
    const response = await fetch(directApiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    console.log(`Minimax API response time: ${Date.now() - startTime}ms`);
    console.log(`Minimax response status: ${response.status}`);
    
    if (!response.ok) {
      let errorMessage = `Minimax API Error: ${response.status}`;
      try {
        const errorData = await response.json();
        console.error('Minimax error response:', errorData);
        
        if (errorData.base_resp) {
          errorMessage = `Minimax error: ${errorData.base_resp.status_msg || 'Unknown error'}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        }
      } catch (e) {
        console.error('Failed to parse error response:', e);
      }
      
      throw new APIError(errorMessage, 'Minimax');
    }
    
    // Get response as text first to inspect it
    const responseText = await response.text();
    console.log(`Minimax response received (length: ${responseText.length} bytes):`);
    console.log(`RAW RESPONSE TEXT: ${responseText}`);
    
    // Try to parse response
    let data: any; // Using any for more flexibility
    try {
      data = JSON.parse(responseText);
      console.log('JSON parsing successful');
    } catch (e) {
      console.error('Failed to parse Minimax API response:', e);
      console.error('Response is not valid JSON. First 100 characters:', responseText.substring(0, 100));
      return "I'm sorry, but I received an invalid response from Minimax API. The response could not be parsed as JSON.";
    }
    
    // Print the full response data for debugging
    console.log('Raw Minimax response object:', data);
    console.log('Response keys:', Object.keys(data));
    
    if (data.id) console.log('Response ID:', data.id);
    if (data.model) console.log('Response model:', data.model);
    if (data.choices) {
      console.log('Choices found:', data.choices.length);
      console.log('First choice:', data.choices[0]);
      if (data.choices[0]?.message) {
        console.log('First choice message:', data.choices[0].message);
        console.log('Content type:', typeof data.choices[0].message.content);
        console.log('Content present:', data.choices[0].message.content !== undefined);
        if (data.choices[0].message.content !== undefined) {
          console.log('Content sample:', typeof data.choices[0].message.content === 'string' ? 
            data.choices[0].message.content.substring(0, 100) : 
            JSON.stringify(data.choices[0].message.content).substring(0, 100));
        }
      } else {
        console.log('Message not found in first choice');
      }
    } else {
      console.log('No choices found in response');
    }
    
    // Log response structure (without sensitive data)
    console.log('Minimax response structure:', {
      hasId: !!data.id,
      objectType: data.object,
      model: data.model,
      hasChoices: !!data.choices,
      choiceCount: data.choices?.length || 0,
      fullData: data // Log everything for debugging
    });
    
    // SUPER permissive response parsing - accept ANY format and try to extract content
    let content = '';
    
    // Try all possible paths to extract content
    if (data.choices && data.choices.length > 0) {
      // Look for content in the standard place
      if (data.choices[0].message && typeof data.choices[0].message.content === 'string') {
        content = data.choices[0].message.content;
        console.log('Found content in data.choices[0].message.content');
      }
      // Try alternative locations
      else if (data.choices[0].content && typeof data.choices[0].content === 'string') {
        content = data.choices[0].content;
        console.log('Found content in data.choices[0].content');
      }
      else if (typeof data.choices[0] === 'string') {
        content = data.choices[0];
        console.log('Found content in data.choices[0] as string');
      }
      else if (data.choices[0].text && typeof data.choices[0].text === 'string') {
        content = data.choices[0].text;
        console.log('Found content in data.choices[0].text');
      }
    }
    
    // Try top-level fields
    if (!content && data.content && typeof data.content === 'string') {
      content = data.content;
      console.log('Found content in data.content');
    }
    else if (!content && data.text && typeof data.text === 'string') {
      content = data.text;
      console.log('Found content in data.text');
    }
    else if (!content && data.message && typeof data.message.content === 'string') {
      content = data.message.content;
      console.log('Found content in data.message.content');
    }
    
    // Last resort - if we couldn't find content naturally
    if (!content) {
      console.error('Could not find content using standard paths, trying alternative ways:', data);
      
      // Use JSON.stringify as a fallback - this will give us SOMETHING to show the user
      try {
        // If we have any choices at all, use the entire first choice as a string
        if (data.choices && data.choices.length > 0) {
          content = "Minimax Response (debug): " + JSON.stringify(data.choices[0]);
        } 
        // Otherwise stringify the whole response
        else {
          content = "Minimax Raw Response (debug): " + JSON.stringify(data).substring(0, 500);
        }
        console.log("Created debug content by stringifying response");
      } catch (e) {
        console.error('Even stringification failed:', e);
        content = "I'm sorry, but I had trouble processing the response from Minimax. Please try asking a different question.";
      }
    }
    
    // Record usage
    if (userId && typeof topUpValue === 'number') {
      try {
        const usage = {
          prompt_tokens: data.usage?.prompt_tokens || estimateTokenCount(formattedMessages.map(m => m.content).join(' ')),
          completion_tokens: data.usage?.completion_tokens || estimateTokenCount(content),
          total_tokens: data.usage?.total_tokens || 0
        };
        
        // Ensure total_tokens is consistent
        if (usage.total_tokens === 0) {
          usage.total_tokens = usage.prompt_tokens + usage.completion_tokens;
        }
        
        await usageService.recordUsage(userId, modelId, usage, topUpValue);
        console.log('Recorded Minimax usage');
      } catch (err) {
        console.error('Failed to record Minimax usage:', err);
      }
    }
    
    return content;
  } catch (error) {
    console.error('Minimax API error:', error);
    
    if (error instanceof APIError) {
      throw error;
    }
    
    throw new APIError(
      error instanceof Error ? error.message : 'Failed to communicate with Minimax',
      'Minimax'
    );
  }
}